import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/he';
import { ApiError } from '../../../Models/ApiModel';
import { PremiumFeature } from '../../../Models/enums';
import type { BusinessSettingsModel, OpenFormatRunModel } from '../../../Models/ReceiptModel';
import { ReceiptService } from '../../../Services/ReceiptService';
import { openFormatDisplayDate, openFormatProducedAt } from '../../../Utils/OpenFormatUtil';
import { Modal } from '../../SharedArea/Modal/Modal';
import { OpenFormatSummary } from './OpenFormatSummary';
import { PremiumGate } from '../Premium/PremiumGate';
import { PremiumSubnav } from '../Premium/PremiumSubnav';
import './OpenFormat.css';

/**
 * §5.4 names every record type the structure defines. A row is shown only for
 * a type the file actually holds, so B100 and M100 are here for the day they
 * exist and never appear until then.
 */
const RECORD_KEYS: Record<string, TranslationKey> = {
  A100: 'openFormatRecordA100',
  B100: 'openFormatRecordB100',
  B110: 'openFormatRecordB110',
  C100: 'openFormatRecordC100',
  D110: 'openFormatRecordD110',
  D120: 'openFormatRecordD120',
  M100: 'openFormatRecordM100',
  Z900: 'openFormatRecordZ900'
};

/** A value dropped into a sentence of the other direction keeps its own. */
const isolate = (value: string): string => `⁨${value}⁩`;

/**
 * ממשק פתוח, as the 1.31 instructions describe the module rather than as a
 * section of some other screen.
 *
 * §2.1 wants it under that name and one step from the main menu, which is why
 * it has a route and a menu entry of its own. §2.1.ג and §5.4 want a dialog on
 * activation and a printable summary when the run ends. None of this is
 * looked at by the simulator and none of it changes a byte of the file: it is
 * what an examiner sees on opening the app.
 */
export function OpenFormat() {
  return (
    <PremiumGate feature={PremiumFeature.Exports}>
      <OpenFormatInner />
    </PremiumGate>
  );
}

function OpenFormatInner() {
  const { tr, language } = useTranslation();
  const year = new Date().getFullYear();
  const [settings, setSettings] = useState<BusinessSettingsModel | null>(null);
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);
  // Activating the module is opening this screen, so the dialog is what opens.
  const [dialogOpen, setDialogOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState<OpenFormatRunModel | null>(null);
  // Two printable sheets share this screen and print the same way, pulled to
  // the top of the page. Only the one that was asked for may print.
  const [printTarget, setPrintTarget] = useState<'run' | 'report'>('report');

  useEffect(() => {
    ReceiptService.getSettings().then(setSettings).catch(() => setSettings(null));
  }, []);

  const valid = Boolean(from && to && from <= to);
  const businessName = (language === 'en' && settings?.businessNameEn) || settings?.businessName || '';
  const vatId = settings?.registrationNumber ?? '';

  async function produce() {
    setBusy(true);
    setError(null);
    try {
      const result = await ReceiptService.downloadOpenFormat(from, to);
      save(result.blob);
      setRun(result);
      setDialogOpen(false);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : String(problem));
    } finally {
      setBusy(false);
    }
  }

  function printRun() {
    flushSync(() => setPrintTarget('run'));
    window.print();
    setPrintTarget('report');
  }

  const produced = run ? openFormatProducedAt(run.path) : null;

  return (
    <section className="openformat-page" data-print={printTarget}>
      <PremiumSubnav />
      <h1 className="screen-title">{tr('openFormatTitle')}</h1>
      <p className="openformat-note">{tr('openFormatBody')}</p>

      <div className="openformat-actions">
        <button type="button" className="primary-button" onClick={() => setDialogOpen(true)}>
          {run ? tr('openFormatRunAgain') : tr('openFormatActivate')}
        </button>
      </div>

      {run && (
        <>
          <div className="openformat-run">
            <dl className="openformat-run-facts">
              <div>
                <dt>{tr('openFormatRunVatId')}</dt>
                <dd><bdi>{vatId}</bdi></dd>
              </div>
              <div>
                <dt>{tr('openFormatRunBusiness')}</dt>
                <dd>{businessName}</dd>
              </div>
            </dl>
            <p className="openformat-run-done">{tr('openFormatRunDone')}</p>
            <p className="openformat-run-line">
              {tr('openFormatRunPath')} <bdi dir="ltr" className="openformat-path">{run.path}</bdi>
            </p>
            <p className="openformat-run-note">{tr('openFormatRunPathNote')}</p>
            <p className="openformat-run-line">
              {tr('openFormatRunRange',
                isolate(openFormatDisplayDate(run.from)),
                isolate(openFormatDisplayDate(run.to)))}
            </p>

            <h3>{tr('openFormatRunRecordsTitle')}</h3>
            <table className="openformat-report-table">
              <thead>
                <tr>
                  <th>{tr('openFormatRunCode')}</th>
                  <th>{tr('openFormatRunDescription')}</th>
                  <th>{tr('openFormatRunCount')}</th>
                </tr>
              </thead>
              <tbody>
                {run.records.map(record => (
                  <tr key={record.code}>
                    <td><bdi>{record.code}</bdi></td>
                    <td>{RECORD_KEYS[record.code] ? tr(RECORD_KEYS[record.code]!) : ''}</td>
                    <td><bdi>{record.count}</bdi></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="openformat-run-line">
              {tr('openFormatRunSoftware',
                isolate(run.softwareName),
                isolate(settings?.softwareRegistryNumber || tr('openFormatRunNoRegistry')),
                isolate(produced?.date ?? ''),
                isolate(produced?.time ?? ''))}
            </p>
          </div>
          <div className="openformat-actions">
            <button type="button" className="ghost-button" onClick={printRun}>{tr('print')}</button>
          </div>

          <section className="openformat-group">
            <h2>{tr('openFormatSummaryTitle')}</h2>
            <OpenFormatSummary from={run.from} to={run.to} />
          </section>
        </>
      )}

      {dialogOpen && (
        <Modal
          title={tr('openFormatTitle')}
          onClose={() => setDialogOpen(false)}
          footer={(
            <>
              <button type="button" className="ghost-button" onClick={() => setDialogOpen(false)}>
                {tr('cancel')}
              </button>
              <button type="button" className="primary-button" onClick={produce} disabled={!valid || busy}>
                {busy ? tr('openFormatRunning') : tr('openFormatDialogRun')}
              </button>
            </>
          )}
        >
          <div className="openformat-dialog">
            <div>
              <h3>{tr('openFormatDialogBusiness')}</h3>
              <p className="openformat-dialog-business">
                {businessName} <bdi>{vatId}</bdi>
              </p>
            </div>

            <div>
              <h3>{tr('openFormatDialogRange')}</h3>
              <div className="openformat-dialog-range">
                <label className="field">
                  <span>{tr('fromDate')}</span>
                  <input className="text-input" type="date" value={from} onChange={event => setFrom(event.target.value)} />
                  <bdi className="openformat-dialog-compact">{openFormatDisplayDate(from)}</bdi>
                </label>
                <label className="field">
                  <span>{tr('toDate')}</span>
                  <input className="text-input" type="date" value={to} onChange={event => setTo(event.target.value)} />
                  <bdi className="openformat-dialog-compact">{openFormatDisplayDate(to)}</bdi>
                </label>
              </div>
              {!valid && <p className="openformat-error">{tr('openFormatRangeError')}</p>}
            </div>

            <div>
              <h3>{tr('openFormatDialogDestination')}</h3>
              <p className="openformat-help">{tr('openFormatDialogDestinationBody')}</p>
              <pre className="openformat-tree" dir="ltr">
{`OPENFRMT.zip
  OPENFRMT\\${(vatId.replace(/\D/g, '').padStart(9, '0')).slice(0, 8)}.${String(year % 100).padStart(2, '0')}\\MMDDhhmm\\
    INI.TXT
    BKMVDATA.zip  (BKMVDATA.TXT)`}
              </pre>
            </div>

            {error && <p className="openformat-error">{error}</p>}
          </div>
        </Modal>
      )}
    </section>
  );
}

function save(blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'OPENFRMT.zip';
  link.click();
  // Revoking immediately would race the download on some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
